import { cn } from "@suns/design-system/src/utils/cn";
import dayjs from "dayjs";

import {
  ChartContainer,
  ChartLegend,
  ChartLegendContent,
  ChartTooltip,
  ChartTooltipContent,
} from "@suns/design-system/src/components/Chart/Chart";

import {
  generateChartTicks,
  uniqueTicks,
  xTicksFormatter,
} from "@/shared/utils/helper-functions";
import { apolloGradeColor } from "@/shared/utils/colors";

import { roundToSignificantDigit } from "@/shared/utils/helper-functions";
import { ChartConfig } from "@suns/design-system";

import {
  YAxis,
  CartesianGrid,
  XAxis,
  ReferenceLine,
  Line,
  Dot,
  ComposedChart,
  Area,
} from "recharts";
import { theme } from "@/shared/theme";
import { nbaSeasonEndDates } from "@/shared/utils/nba-season";
import { Timeframe } from "@/shared/const";

export interface ChartData {
  snapshotDate: string;
  [key: string]: string | number | null;
}

interface YAxisConfig {
  minValue?: number;
  maxValue?: number;
  minTickCount?: number;
  customTick?:
    | React.ReactElement
    | ((props: {
        x: number;
        y: number;
        payload: { value: number };
      }) => React.ReactElement)
    | undefined;
}

export function MixedChart({
  chartData,
  chartConfig,
  activeChart,
  chartType,
  yAxisConfig,
  useApolloGradeColors = false,
  timeframe,
  showFutureProjections,
}: {
  chartData: ChartData[];
  chartConfig: ChartConfig;
  activeChart: keyof typeof chartConfig | null;
  chartType?: "currency" | null;
  yAxisConfig?: YAxisConfig;
  useApolloGradeColors?: boolean;
  timeframe: Timeframe;
  showFutureProjections: boolean;
}) {
  const allChartValues = chartData.flatMap((d) =>
    Object.keys(chartConfig).map((key) => Number(d[key]))
  );

  const minTicksValue = Math.min(
    0,
    roundToSignificantDigit(
      Math.min(...allChartValues) > 0
        ? Math.min(...allChartValues)
        : Math.min(...allChartValues) * 1.5
    )
  );

  const maxTicksValue = roundToSignificantDigit(
    Math.max(...allChartValues) * 1.5
  );

  const ticks = generateChartTicks(
    yAxisConfig?.minValue ?? minTicksValue,
    yAxisConfig?.maxValue ?? maxTicksValue,
    yAxisConfig?.minTickCount ?? 8
  );

  const branchDate =
    timeframe === Timeframe.FIVE_SEASONS || timeframe === Timeframe.MAX
      ? Object.values(nbaSeasonEndDates)
          .map((dateStr: string) => dayjs(dateStr).utc())
          .pop()
      : dayjs().utc().format("MMM DD YYYY");

  const chartDataWithProjectionBounds = !activeChart
    ? chartData
    : chartData.map((d) => {
        // Skip adding bounds for PPV-related metrics until we have the data
        if (
          activeChart === "ppv" ||
          activeChart === "oPpv" ||
          activeChart === "dPpv" ||
          activeChart === "hybrid" ||
          activeChart === "scout"
        ) {
          return d;
        }

        const isBeforeBranch = dayjs(d.snapshotDate)
          .utc()
          .isBefore(dayjs(branchDate).utc());

        const isEqualBranch = dayjs(d.snapshotDate)
          .utc()
          .isSame(dayjs(branchDate).utc());

        const result = {
          ...d,
          [`${activeChart}_upperBound`]: isBeforeBranch
            ? null
            : isEqualBranch
              ? Number(d[activeChart])
              : d[`${activeChart}_upperBound`]
                ? Number(d[`${activeChart}_upperBound`])
                : null,
          [`${activeChart}_value_range`]: isBeforeBranch
            ? null
            : isEqualBranch
              ? Number(d[`${activeChart}_upperBound`]) -
                Number(d[`${activeChart}_lowerBound`])
              : d[`${activeChart}_lowerBound`]
                ? Number(d[`${activeChart}_lowerBound`]) -
                  Number(d[`${activeChart}_upperBound`])
                : null,
          [`${activeChart}_lowerBound`]: isBeforeBranch
            ? null
            : isEqualBranch
              ? Number(d[activeChart])
              : d[`${activeChart}_lowerBound`]
                ? Number(d[`${activeChart}_lowerBound`])
                : null,
        };

        return result;
      });

  const gradientId = `aimRangeFill-${activeChart ?? "default"}`;

  return (
    <ChartContainer config={chartConfig} className="h-[400px] w-full md:h-full">
      <ComposedChart
        accessibilityLayer
        data={chartDataWithProjectionBounds}
        margin={{
          top: 12,
          left: 20,
          right: 12,
        }}
        syncId="chart"
        syncMethod="value"
      >
        <defs>
          <linearGradient id={gradientId} x1="0" y1="0" x2="0" y2="1">
            <stop
              offset="0%"
              stopColor={
                activeChart
                  ? chartConfig[activeChart]?.color
                  : theme.colors.gray[400]
              }
              stopOpacity={1}
            />
            <stop
              offset="100%"
              stopColor={
                activeChart
                  ? chartConfig[activeChart]?.color
                  : theme.colors.gray[400]
              }
              stopOpacity={1}
            />
          </linearGradient>
        </defs>
        <CartesianGrid vertical={false} />
        <XAxis
          dataKey="snapshotDate"
          tickMargin={6}
          fontSize={8}
          tickFormatter={(value) => {
            return xTicksFormatter(value, timeframe, showFutureProjections);
          }}
          ticks={uniqueTicks(chartDataWithProjectionBounds, timeframe)}
        />
        <YAxis
          width={chartType !== "currency" ? 10 : 30}
          interval={0}
          dataKey={Object.keys(chartConfig)[0]}
          type="number"
          domain={[
            yAxisConfig?.minValue ?? minTicksValue,
            yAxisConfig?.maxValue ?? maxTicksValue,
          ]}
          ticks={ticks}
          tick={yAxisConfig?.customTick ? yAxisConfig?.customTick : true}
          tickFormatter={(value) => {
            if (chartType === "currency") {
              return value.toLocaleString("en-US", {
                style: "currency",
                currency: "USD",
                notation: "compact",
                compactDisplay: "short",
              });
            }
            return value;
          }}
          tickLine={false}
        />
        <ReferenceLine
          y={0}
          stroke={theme.colors.gray[800]}
          strokeWidth={1.5}
          strokeOpacity={0.65}
        />
        <ChartTooltip
          content={
            <ChartTooltipContent
              activeChart={activeChart}
              labelFormatter={(value, payload) => {
                const date = payload[0]?.payload?.snapshotDate;
                return date ? dayjs(date).format("MMM DD YYYY") : value;
              }}
              timeframe={timeframe}
              valueFormatter={(value) => {
                if (chartType === "currency") {
                  const USDollar = new Intl.NumberFormat("en-US", {
                    style: "currency",
                    currency: "USD",
                    maximumFractionDigits: 0,
                  });
                  return USDollar.format(Number(value));
                }
                return Number(value).toFixed(2);
              }}
            />
          }
        />
        {showFutureProjections && activeChart && (
          <>
            {chartDataWithProjectionBounds.some((d) => {
              const isToday = dayjs(d.snapshotDate)
                .utc()
                .isSame(dayjs().utc(), "day");
              return !isToday;
            }) && (
              <>
                <Area
                  dataKey={`${activeChart}_upperBound`}
                  type="monotone"
                  stroke={chartConfig[activeChart]?.color}
                  strokeWidth={2}
                  fillOpacity={0.5}
                  fill="none"
                  animationDuration={900}
                  connectNulls={true}
                  stackId="1"
                  strokeDasharray="3 3"
                  activeDot={({ ...props }) => {
                    const color = chartConfig[activeChart]?.color;
                    return (
                      <Dot
                        key={`${props.cx}-${props.cy}`}
                        r={6}
                        cx={props.cx}
                        cy={props.cy}
                        fill={color}
                        stroke={color}
                      />
                    );
                  }}
                />

                <Area
                  dataKey={
                    activeChart ? `${activeChart}_value_range` : "value_range"
                  }
                  type="monotone"
                  stroke="none"
                  strokeWidth={2}
                  fill={`url(#${gradientId})`}
                  animationDuration={900}
                  connectNulls={true}
                  stackId="1"
                  fillOpacity={0.3}
                />

                <Area
                  dataKey={`${activeChart}_lowerBound`}
                  type="monotone"
                  stroke={chartConfig[activeChart]?.color}
                  strokeWidth={2}
                  fillOpacity={0.5}
                  fill="none"
                  animationDuration={900}
                  connectNulls={true}
                  strokeDasharray="3 3"
                  activeDot={({ ...props }) => {
                    const color = chartConfig[activeChart]?.color;
                    return (
                      <Dot
                        key={`${props.cx}-${props.cy}`}
                        r={6}
                        cx={props.cx}
                        cy={props.cy}
                        fill={color}
                        stroke={color}
                      />
                    );
                  }}
                />
              </>
            )}
          </>
        )}

        <ChartLegend content={<ChartLegendContent />} />

        {Object.entries(chartConfig)
          .filter(
            ([, config]) =>
              !config.isUpperBound &&
              !config.isLowerBound &&
              !config.isValueRange
          )
          .map(([key, config], index) => {
            return (
              <Line
                key={key + index}
                dataKey={key}
                type="monotone"
                stroke={config.color}
                className={cn(
                  activeChart && activeChart !== key && "opacity-25"
                )}
                strokeWidth={2}
                animationDuration={900}
                connectNulls={true}
                activeDot={activeChart === key ? { r: 6 } : false}
                dot={({ payload, ...props }) => {
                  const value = payload[key];
                  return (
                    <Dot
                      key={`${key}-${props.cx}-${props.cy}`}
                      r={4}
                      cx={props.cx}
                      cy={props.cy}
                      fill={
                        useApolloGradeColors
                          ? apolloGradeColor(value, "hex")
                          : config.color
                      }
                      stroke={config.color}
                    />
                  );
                }}
              />
            );
          })}
      </ComposedChart>
    </ChartContainer>
  );
}
